import { SupportedLocation } from '@/modules/location/constants';

function isValidTimeZoneAbbreviation(value: string): boolean {
  return (
    value.length >= 2 && value.length <= 6 && /^[A-Z\+\-0-9]+$/.test(value)
  );
}

export function getShortTimeZoneName(
  ianaTimeZone: string,
  date: Date = new Date()
): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaTimeZone,
      timeZoneName: 'short',
    });

    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find((part) => part.type === 'timeZoneName');
    let abbreviation = timeZonePart?.value;

    if (abbreviation) {
      abbreviation = abbreviation.toUpperCase();

      if (abbreviation === 'GMT' || abbreviation.includes('GMT')) {
        return abbreviation;
      }

      if (isValidTimeZoneAbbreviation(abbreviation)) {
        return abbreviation;
      }
    }

    const formatted = formatter.format(date);
    const match = formatted.match(/\s([A-Z\+\-0-9]+)$/);
    const extractedAbbreviation = match?.[1]?.toUpperCase();

    if (
      extractedAbbreviation &&
      isValidTimeZoneAbbreviation(extractedAbbreviation)
    ) {
      return extractedAbbreviation;
    }

    return '';
  } catch {
    return '';
  }
}

export function formatDropDate(
  date: string,
  timeZoneName: string,
  location: SupportedLocation
): string {
  try {
    const match = date.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/
    );

    if (!match) {
      throw new Error(`Invalid date string format: ${date}`);
    }

    const [, _year, month, day, hour, _minute] = match;
    const monthIndex = parseInt(month, 10) - 1;
    const hours = parseInt(hour, 10);
    const dayNum = parseInt(day, 10);

    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const monthName = monthNames[monthIndex];

    let displayHour = hours;
    let dayPeriod = 'AM';

    if (hours === 0) {
      displayHour = 12;
    } else if (hours === 12) {
      dayPeriod = 'PM';
    } else if (hours > 12) {
      displayHour = hours - 12;
      dayPeriod = 'PM';
    }

    const time = `${displayHour}${dayPeriod}`;
    const timeZoneSuffix = timeZoneName ? ` ${timeZoneName}` : '';
    if (location === SupportedLocation.US) {
      return `${monthName} ${dayNum}, ${time}${timeZoneSuffix}`;
    }

    let ordinalSuffix = 'th';
    if (dayNum % 10 === 1 && dayNum !== 11) {
      ordinalSuffix = 'st';
    } else if (dayNum % 10 === 2 && dayNum !== 12) {
      ordinalSuffix = 'nd';
    } else if (dayNum % 10 === 3 && dayNum !== 13) {
      ordinalSuffix = 'rd';
    }

    return `${dayNum}${ordinalSuffix} ${monthName}, ${time}${timeZoneSuffix}`;
  } catch {
    return '';
  }
}
